import React, { useMemo, useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { AxiosError, isAxiosError } from "axios";
import { Controller, FieldError, FormProvider, useForm } from "react-hook-form";
import { v4 as uuid } from "uuid";

import {
  ManifestIntegrationProvider,
  ManifestResourceDefinition,
} from "src/api/connectApi/manifestTypes";
import {
  useCreateManifestConnection,
  useUpdateManifestConnection,
} from "src/api/connectApi/queries";
import {
  Connection,
  CredentialRefreshProviders,
} from "src/api/connectApi/types";
import { WorkspaceWithSettings } from "src/api/types";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { Switch } from "src/base-components/Switch";
import { TextConfirmationModal } from "src/base-components/TextConfirmationModal";
import { ApiEndpointsAccordion } from "src/connections/config/ApiEndpointsAccordion";
import { DataRetentionFields } from "src/connections/config/DataRetentionFields";
import { getValidationRules } from "src/connections/config/manifest/common/ValidationRules";
import { ManifestEnvironmentsConfig } from "src/connections/config/manifest/ManifestEnvironmentsConfig";
import {
  isManifestResourceAvailableInAWSRegion,
  isManifestResourceVisibleByAudience,
  ResourceConfigForm,
} from "src/connections/config/manifest/ResourceConfigForm";
import {
  FormPath,
  ManifestFormType,
  ManifestResourceCreate,
} from "src/connections/config/manifest/types";
import { convertBEConnectionToManifestConnectionInput } from "src/connections/model/manifest";
import { useConnectionManagementActions } from "src/connections/store/connectionManagementStore";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { isConflictError } from "src/utils/predicates";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";

// Creates a default resource object for the form
const createResource = ([resource_key, resource_definition]: [
  string,
  ManifestResourceDefinition,
]): ManifestResourceCreate => {
  return {
    id: uuid(),
    name: resource_definition.display_name,
    resource: resource_key,
    has_raw_response_enabled: false,
    has_raw_requests_enabled_in_resource: false,
    disable_sensitive_data_stripping: false,
    configuration: {},
  };
};

type ManifestForm = {
  onCancelClick: () => void;
  manifest: ManifestIntegrationProvider;
  workspace: WorkspaceWithSettings;
  resetFormStates: () => void;
  onClose: () => void;
  connectionToEdit?: Connection;
};

export const ManifestForm: React.FC<ManifestForm> = ({
  manifest,
  onCancelClick,
  onClose,
  workspace,
  resetFormStates,
  connectionToEdit,
}) => {
  const { setConnectionToEdit, setModalOpen } =
    useConnectionManagementActions();
  const workspaceData = useWorkspaceAndOrg();
  const AWSRegion = workspaceData.workspace?.aws_region;
  const [resourceToDelete, setResourceToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const defaultResourceDefinition = Object.entries(manifest.resources).find(
    ([_, resource]) =>
      isManifestResourceAvailableInAWSRegion(resource, AWSRegion) &&
      isManifestResourceVisibleByAudience(resource, manifest.audience),
  );

  const defaultValues = useMemo(() => {
    if (connectionToEdit) {
      return convertBEConnectionToManifestConnectionInput(connectionToEdit);
    } else {
      return {
        name: `${manifest.display_name} connection`,
        resource_configs: [createResource(defaultResourceDefinition!)],
        inputs: {},
        disable_credential_bg_jobs: false,
      };
    }
  }, [connectionToEdit, manifest, defaultResourceDefinition]);

  const createConnection = useCreateManifestConnection(workspace.base_url);
  const editConnection = useUpdateManifestConnection(workspace.base_url);
  const formMethods = useForm<ManifestFormType>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues,
  });
  const {
    formState: { isSubmitting: formIsSubmitting, errors: formErrors },
    handleSubmit: handleFormSubmit,
    watch,
    setValue: setFormValue,
  } = formMethods;

  const formValue = watch();

  const validateFullForm = (data: ManifestFormType): boolean => {
    const errors: Record<string, FieldError> = {};
    if (data.inputs) {
      Object.entries(data.inputs).forEach(([environment, values]) => {
        const properties =
          manifest.environment_configs[environment]?.inputs?.properties;
        if (!properties) {
          return true;
        }

        Object.entries(properties).forEach(([key, schema]) => {
          const isRequired =
            manifest.environment_configs[
              environment
            ].inputs?.required?.includes(key);
          const validationRules = getValidationRules(schema, !!isRequired, key);
          const validationResult = validationRules.validate(values[key]);
          if (validationResult !== true) {
            errors[`inputs.${environment}.${key}`] = {
              type: "validation",
              message: validationResult,
            };
          }
        });
      });
    }

    if (Object.keys(errors).length > 0) {
      Object.entries(errors).forEach(([path, error]) => {
        formMethods.setError(path as FormPath, error);
      });
      return false;
    }
    return true;
  };

  const transformResourceInstance = (
    currentResourceInstance: ManifestResourceCreate,
    newResourceName: string,
  ) => {
    // Switches the resource type of the current resource instance to the new one
    const newResource = createResource([
      newResourceName,
      manifest.resources[newResourceName],
    ]);

    // If the user changed the current resource's name, keep it
    // otherwise, use the default for the new resource
    const currentResourceDefaultName =
      manifest.resources[currentResourceInstance.resource].display_name;
    if (currentResourceInstance.name !== currentResourceDefaultName)
      newResource.name = currentResourceInstance.name;
    return newResource;
  };

  const onlyOneResourceInConnection = formValue.resource_configs.length <= 1;

  const handleAddResource = () => {
    const newResource = createResource(defaultResourceDefinition!);
    setFormValue("resource_configs", [
      ...formValue.resource_configs,
      newResource,
    ]);
  };

  const handleRemoveResource = (resourceId: string) => {
    const resource = formValue.resource_configs.find(
      (r) => r.id === resourceId,
    );
    if (resource) {
      setResourceToDelete({ id: resourceId, name: resource.name });
    }
  };

  const confirmRemoveResource = () => {
    if (resourceToDelete) {
      setFormValue(
        "resource_configs",
        formValue.resource_configs.filter(
          (resource) => resource.id !== resourceToDelete.id,
        ),
      );
      setResourceToDelete(null);
    }
  };

  const onSelectResource = (
    currentResourceInstance: ManifestResourceCreate,
    newResource: string,
  ) => {
    const newResourceInstance = transformResourceInstance(
      currentResourceInstance,
      newResource,
    );
    const newResourceConfigArray = [...formValue.resource_configs];
    const oldResourceIndex = newResourceConfigArray.findIndex(
      (element) => element.id === currentResourceInstance.id,
    );
    newResourceConfigArray[oldResourceIndex] = newResourceInstance;
    setFormValue("resource_configs", newResourceConfigArray);
  };

  const onSubmitCreateConnection = async (
    data: ManifestFormType,
  ): Promise<boolean> => {
    if (connectionToEdit) {
      throw new Error(
        "Unexpected function call, use 'onSubmitEditConnection' for editing connections",
      );
    }

    try {
      const result = await createConnection.mutateAsync({
        ...data,
        provider: manifest.name,
        manifest_version: manifest.version,
      });
      const toastId = toastActions.success({
        title: "Connection created",
        description: "Connection was created successfully",
        actionText: "View connection",
        onActionClick: () => {
          toastActions.dismiss(toastId);
          setConnectionToEdit(result);
          setModalOpen(true);
        },
      });

      return true;
    } catch (e) {
      if (isConflictError(e)) {
        toastActions.failure({
          title: "Connection creation failed",
          description: (e as AxiosError<{ detail: string }>).response?.data
            .detail,
          duration: Infinity,
        });
      } else {
        toastActions.failure({
          title: "Connection creation failed",
          description: "Connection could not be created.",
        });
      }

      return false;
    }
  };

  const onSubmitEditConnection = async (
    data: ManifestFormType,
  ): Promise<boolean> => {
    if (!connectionToEdit) {
      throw new Error(
        "Unexpected function call, use 'onSubmitCreateConnection' for creating connections",
      );
    }

    try {
      await editConnection.mutateAsync({
        connectionId: connectionToEdit.id,
        payload: {
          ...data,
          provider: manifest.name,
          manifest_version: manifest.version,
        },
      });
      toastActions.success({
        title: "Connection updated",
        description: "Connection was updated successfully",
      });

      return true;
    } catch (e) {
      if (isConflictError(e)) {
        toastActions.failure({
          title: "Connection update failed",
          description: (e as AxiosError<{ detail: string }>).response?.data
            .detail,
          duration: Infinity,
        });
      } else {
        toastActions.failure({
          title: "Connection edit failed",
          description:
            isAxiosError(e) && e.response?.data?.detail?.msg
              ? e.response.data.detail.msg
              : `Connection ${connectionToEdit.name} could not be edited`,
        });
      }

      return false;
    }
  };

  const onSubmit = async (data: ManifestFormType) => {
    formMethods.clearErrors();

    if (!validateFullForm(data)) {
      return;
    }

    let success;
    if (connectionToEdit) {
      success = await onSubmitEditConnection(data);
    } else {
      success = await onSubmitCreateConnection(data);
    }

    if (success) {
      onClose();
      resetFormStates();
    }
  };

  const isPowerToolsEnabled = isFeatureFlagEnabled(FEATURE_FLAGS.powertools);
  const isSlackConnection = manifest.name === "slack";
  const shouldShowConnectionName =
    isPowerToolsEnabled || isSlackConnection || manifest.is_generic_tool;
  return (
    <FormProvider {...formMethods}>
      <form
        className="relative"
        data-loc="manifest-connection-config-form"
        onSubmit={handleFormSubmit(onSubmit)}
      >
        <Modal.Content>
          {shouldShowConnectionName && (
            <>
              <FormItem
                key="name"
                gap="sm"
                id="manifest-connection-form_name"
                isRequired={true}
                label="Connection Name"
              >
                {formErrors.name && (
                  <ErrorHint>Connection name is required</ErrorHint>
                )}
                <div className="flex w-full">
                  <div className="flex-1">
                    <Input
                      data-loc="manifest-connection-config-form-name"
                      errored={Boolean(formErrors.name)}
                      placeholder="Connection name"
                      type="text"
                      fullWidth
                      required
                      {...formMethods.register("name", {
                        required: true,
                      })}
                    />
                  </div>
                </div>
              </FormItem>
            </>
          )}

          {isPowerToolsEnabled &&
            CredentialRefreshProviders.includes(manifest.name) && (
              <FormItem
                className="flex items-center justify-between"
                description="When enabled, Taktile will not run background credential refresh logic for this connection."
                label="Disable credential refresh"
              >
                <Controller
                  control={formMethods.control}
                  name="disable_credential_bg_jobs"
                  render={({ field: { value, onChange } }) => (
                    <div className="ml-1">
                      <Switch
                        dataLoc="disable-credential-bg-jobs-switch"
                        enabled={!!value}
                        onChange={() => onChange(!value)}
                      />
                    </div>
                  )}
                />
              </FormItem>
            )}
          <ManifestEnvironmentsConfig
            connectionToEdit={connectionToEdit}
            formValue={formValue}
            manifest={manifest}
            setFormValue={setFormValue}
          />

          {Object.entries(formValue.resource_configs).map(
            ([key, resource], i) => (
              <ResourceConfigForm
                key={key}
                index={i}
                manifest={manifest}
                onlyOneResourceInConnection={onlyOneResourceInConnection}
                resource={resource}
                onRemove={handleRemoveResource}
                onSelectResource={onSelectResource}
              />
            ),
          )}
          <div>
            <Button
              dataLoc="resource-add-button"
              iconLeft={faPlus}
              size="sm"
              variant="secondary"
              onClick={handleAddResource}
            >
              Add resource
            </Button>
          </div>
          <hr className="mt-6 mb-8 h-px w-full bg-gray-100" />
          <DataRetentionFields
            fieldName="data_retention"
            workspace={workspace}
          />
          <ApiEndpointsAccordion variant="manifest" manifest={manifest} />
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="manifest-connection-save"
              htmlType="submit"
              isLoading={formIsSubmitting}
              variant="primary"
            >
              Save
            </Button>
          }
          secondaryButton={
            <Button
              isDisabled={formIsSubmitting}
              variant="secondary"
              onClick={onCancelClick}
            >
              Cancel
            </Button>
          }
        />
      </form>
      <TextConfirmationModal
        challengeText={resourceToDelete?.name}
        description="Are you sure you want to delete this resource? This action is not reversible, and might affect your existing API integrations."
        label="Type resource name"
        open={resourceToDelete !== null}
        title={`Confirm delete ${resourceToDelete?.name}`}
        onClose={() => setResourceToDelete(null)}
        onConfirm={confirmRemoveResource}
      />
    </FormProvider>
  );
};
